import api from './api'

export enum VerificationCodeType {
  EMAIL_VERIFICATION = 'EMAIL_VERIFICATION',
  PHONE_VERIFICATION = 'PHONE_VERIFICATION',
  IDENTITY_VERIFICATION = 'IDENTITY_VERIFICATION',
  PASSWORD_RESET = 'PASSWORD_RESET',
  COMPANY_VERIFICATION = 'COMPANY_VERIFICATION',
}

export interface CreateVerificationCodeDto {
  type: VerificationCodeType
  email: string
  metadata?: string
}

export interface VerifyCodeDto {
  code: string
  email: string
  type: VerificationCodeType
}

export interface ResendCodeDto {
  email: string
  type: VerificationCodeType
}

export interface VerificationCodeResponse {
  success: boolean
  message: string
  data?: any
}

export interface BuroAuthorizationRequest {
  code: string
  email: string
  type: VerificationCodeType
  reportType?: string
}

export interface BuroAuthorizationResponse {
  success: boolean
  message: string
  reportId?: string
  rfc?: string
  taxpayerType?: string
  reportType?: string
  status?: string
  reportSummary?: any
  reportDate?: Date
  metadata?: Record<string, any>
  data?: Record<string, any>
}

class VerificationCodeService {
  async createCode(
    data: CreateVerificationCodeDto
  ): Promise<VerificationCodeResponse> {
    const response = await api.post('/verification-codes', data)
    return response.data
  }

  async verifyCode(data: VerifyCodeDto): Promise<VerificationCodeResponse> {
    const response = await api.post('/verification-codes/verify', data)
    return response.data
  }

  async resendCode(data: ResendCodeDto): Promise<VerificationCodeResponse> {
    const response = await api.post('/verification-codes/resend', data)
    return response.data
  }

  // Helper method for credit bureau consultation NIP verification
  async sendCreditBureauNIP(email: string): Promise<VerificationCodeResponse> {
    return this.createCode({
      type: VerificationCodeType.COMPANY_VERIFICATION,
      email,
      metadata: JSON.stringify({
        purpose: 'credit_bureau_nip',
        context: 'company_consultation',
      }),
    })
  }

  // Helper method to verify credit bureau NIP
  async verifyCreditBureauNIP(
    code: string,
    email: string
  ): Promise<VerificationCodeResponse> {
    return this.verifyCode({
      code,
      email,
      type: VerificationCodeType.COMPANY_VERIFICATION,
    })
  }

  // Verify and authorize PF (Persona Física) credit bureau consultation
  async verifyAndAuthorizePF(
    data: BuroAuthorizationRequest
  ): Promise<BuroAuthorizationResponse> {
    const response = await api.post(
      '/verification-codes/verify-and-authorize-pf',
      data,
      { timeout: 60000 } // La consulta a Buró puede tardar más que el timeout global
    )

    // Handle nested success structure when code is incorrect
    if (
      response.data?.success === true &&
      response.data?.data?.success === false
    ) {
      return {
        success: false,
        message: response.data.data.message || 'Código incorrecto',
        metadata: response.data.data.metadata,
      }
    }

    return response.data
  }

  // Verify and authorize PM (Persona Moral) credit bureau consultation
  async verifyAndAuthorizePM(
    data: BuroAuthorizationRequest
  ): Promise<BuroAuthorizationResponse> {
    const response = await api.post(
      '/verification-codes/verify-and-authorize-pm',
      data,
      { timeout: 60000 } // La consulta a Buró puede tardar más que el timeout global
    )

    // Handle nested success structure when code is incorrect
    if (
      response.data?.success === true &&
      response.data?.data?.success === false
    ) {
      return {
        success: false,
        message: response.data.data.message || 'Código incorrecto',
        metadata: response.data.data.metadata,
      }
    }

    return response.data
  }
}

export default new VerificationCodeService()
